import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { FreeMode, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import { useTranslation } from "react-i18next";

type PropsType = {
  isTextVisible: boolean;
  swiperRef: React.LegacyRef<SwiperRef> | undefined;
  setActiveIndex: (activeIndex: number) => void;
};

const AchievementSwiper: React.FC<PropsType> = ({ isTextVisible, swiperRef, setActiveIndex }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const swiperList = [
    {
      title: t("home.achieve.swiper_list.title1"),
      content: t("home.achieve.swiper_list.content1"),
      img: require("src/assets/images/home/swiper1.jpg"),
    },
    {
      title: t("home.achieve.swiper_list.title2"),
      content: t("home.achieve.swiper_list.content2"),
      img: require("src/assets/images/home/swiper2.jpg"),
    },
    {
      title: t("home.achieve.swiper_list.title3"),
      content: t("home.achieve.swiper_list.content3"),
      img: require("src/assets/images/home/swiper3.jpg"),
    },
    {
      title: t("home.achieve.swiper_list.title4"),
      content: t("home.achieve.swiper_list.content4"),
      img: require("src/assets/images/home/swiper4.jpg"),
    },
  ];

  return (
    <Box
      sx={{
        cursor: isTextVisible ? "auto" : "grab",
        width: "clamp(208px,65vw,1248px)",
        display: "flex",
        flex: "none",
        position: "relative",
        overflow: "clip",
        pointerEvents: "all",
        transform: "translate3d(0px, -8.982vh, 0px)",
        [theme.breakpoints.down("md")]: { width: "100%" },
        ".swiper-wrapper": {
          width: "100%",
          display: "flex",
          alignItems: "center",
          transitionProperty: "transform",
          position: "relative",
          zIndex: 1,
        },
        ".swiper-slide": {
          width: "clamp(208px,30vw,576px)",
          userSelect: "none",
          position: "relative",
          display: "flex",
          flexShrink: 0,
        },
        ".swiper-slide:not(.swiper-slide-active)": { transition: "all .3s", opacity: isTextVisible ? 0 : 1 },
        ".swiper-slide-active": {
          ".item-thumb": { filter: "none" },
          ">.MuiBox-root": {
            bgcolor: isTextVisible ? "transparent" : "#f4f4f6",
            transform: "none",
            transition: "all .6s",
            img: {
              transform: isTextVisible ? "scale(1.5)translate(0)" : "none",
              transition: "all .6s",
            },
            ".MuiTypography-root": {
              opacity: isTextVisible ? 0 : 1,
              "&:nth-of-type(1)": { fontSize: "clamp(22px,1.667vw,32px)" },
              "&:nth-of-type(2)": { display: "block" },
            },
          },
        },
        ".swiper-slide-next": { ">.MuiBox-root": { transformOrigin: "center" } },
      }}
    >
      <Swiper
        ref={swiperRef}
        loop
        autoplay={{ delay: 6000, disableOnInteraction: false }}
        slidesPerView={2}
        spaceBetween={0}
        freeMode={true}
        modules={[Autoplay, FreeMode]}
        onSlideChange={swiper => setActiveIndex(swiper.realIndex)}
      >
        {swiperList.map((item, index) => (
          <SwiperSlide key={index}>
            <Box
              sx={{
                width: "100%",
                bgcolor: "#f4f4f6",
                m: "0 auto !important",
                borderRadius: "clamp(4px,.417vw,8px)",
                p: "clamp(32px,3.125vw,60px) clamp(20px,3.125vw,60px)",
                display: "flex",
                flexDirection: "column",
                gap: "clamp(60px,4.167vw,80px)",
                transform: "scale(.5)",
                transformOrigin: "0",
              }}
            >
              <Box
                className="item-thumb"
                sx={{
                  width: "100%",
                  aspectRatio: "456/381",
                  filter: "grayscale()",
                  transition: "all .6s",
                  img: { height: "100%", width: "100%", objectFit: "cover" },
                }}
              >
                <img src={item.img} alt="achievement" />
              </Box>
              <Box sx={{ gap: "20px", transition: "opacity .6s", display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 500, lineHeight: 1.46 }}>{item.title}</Typography>
                <Typography sx={{ fontSize: "clamp(14px,.833vw,16px)", fontWeight: 400, lineHeight: 1.6, display: "none" }}>
                  {item.content}
                </Typography>
              </Box>
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default AchievementSwiper;
